import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import {
  setDefaultWindowMinutes,
  DEFAULT_WINDOW_MINUTES,
} from "@/components/orders/orderFormTypes";

/** React Query key factory for `app_settings` rows so callers can invalidate
 *  a single key after a mutation. */
export const appSettingKey = (key: string) => ["app-setting", key] as const;

/** Read a single row from `public.app_settings` by key. Returns the raw
 *  jsonb `value` cast to `T`, or `undefined` when the row is missing.
 *  Cached for 5 minutes since these values change rarely.
 *
 *  Gated on the session. `app_settings` grants SELECT to `authenticated` only
 *  (there is no anon grant, and the SELECT policy requires `is_internal_user()`),
 *  so a fetch before the session exists is a guaranteed `42501 permission
 *  denied`. `OrderWindowMinutesSync` is mounted outside `<Routes>`, which means
 *  it runs on the unauthenticated `/auth` page — the same pre-session race
 *  `useTenant` documents. Without the gate the query also sits in an error state
 *  for the rest of the session, so the setting kept its module fallback instead
 *  of the stored value. */
export function useAppSetting<T = unknown>(key: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: appSettingKey(key),
    enabled: !!user?.id,
    queryFn: async (): Promise<T | undefined> => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", key)
        .maybeSingle();
      if (error) throw error;
      return (data?.value as T | undefined) ?? undefined;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Hook wrapper around the `order_window_minutes` setting. Falls back to the
 *  current module-level `DEFAULT_WINDOW_MINUTES` (60 on first boot) so the
 *  order form is never blocked waiting on the fetch. */
export function useOrderWindowMinutes(): {
  minutes: number;
  isLoading: boolean;
} {
  const { data, isLoading } = useAppSetting<number>("order_window_minutes");
  const minutes = typeof data === "number" && Number.isFinite(data)
    ? data
    : DEFAULT_WINDOW_MINUTES;
  return { minutes, isLoading };
}

/** Mount once at app boot. Subscribes to the `order_window_minutes` setting
 *  and pushes it into the `DEFAULT_WINDOW_MINUTES` live binding used by the
 *  order form helpers. Renders nothing. */
export function OrderWindowMinutesSync(): null {
  const { minutes } = useOrderWindowMinutes();
  useEffect(() => {
    setDefaultWindowMinutes(minutes);
  }, [minutes]);
  return null;
}

/** Hook wrapper around the `taas_address_snap_enabled` kill switch used by
 *  the TaaS create-order edge function. Defaults to `true` while loading
 *  or when the row is absent so the UI matches the server default. */
export function useAddressSnapEnabled(): {
  enabled: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useAppSetting<{ enabled?: boolean } | boolean>(
    "taas_address_snap_enabled",
  );
  let enabled = true;
  if (typeof data === "boolean") enabled = data;
  else if (data && typeof data === "object" && typeof data.enabled === "boolean") {
    enabled = data.enabled;
  }
  return { enabled, isLoading };
}

/** Convenience helper for the admin card: upsert a setting row and invalidate
 *  its cached query in one call. */
export function useAppSettingMutation() {
  const qc = useQueryClient();
  return async (key: string, value: unknown) => {
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key, value: value as never, updated_at: new Date().toISOString() });
    if (error) throw error;
    await qc.invalidateQueries({ queryKey: appSettingKey(key) });
  };
}